import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Clock, AlertCircle, AlertTriangle, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useSlotAvailability } from "@/hooks/useSlotAvailability";
import { useRescheduleLimits, incrementRescheduleCount } from "@/hooks/useRescheduleLimits";
import { useAuth } from "@/hooks/useAuth";
import type { UserPickup } from "@/types/database";

interface EditPickupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pickup: UserPickup;
  onSuccess: () => void;
}

const TIME_SLOTS = [
  { value: "9:00 AM - 12:00 PM", label: "Morning (9 AM - 12 PM)" },
  { value: "12:00 PM - 3:00 PM", label: "Afternoon (12 PM - 3 PM)" },
  { value: "3:00 PM - 6:00 PM", label: "Evening (3 PM - 6 PM)" },
];

type DialogStep = "consent" | "select";

export const EditPickupDialog = ({
  open,
  onOpenChange,
  pickup,
  onSuccess,
}: EditPickupDialogProps) => {
  const { user } = useAuth();
  
  // Reschedule limits
  const { 
    canReschedule, 
    remainingReschedules, 
    maxReschedules, 
    loading: limitsLoading 
  } = useRescheduleLimits(user?.id);
  
  // Step state
  const [step, setStep] = useState<DialogStep>("consent");
  const [consentChecked, setConsentChecked] = useState(false);
  
  // Form state
  const [newDate, setNewDate] = useState<Date | undefined>();
  const [newTimeSlot, setNewTimeSlot] = useState<string>("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const { availability, loading: checkingAvailability, checkDateAvailability, isSlotAvailable, getSlotStatus } = useSlotAvailability();

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setStep("consent");
      setConsentChecked(false);
      setNewDate(undefined);
      setNewTimeSlot("");
      setReason("");
    }
  }, [open]);

  // Check availability when date changes
  useEffect(() => {
    if (newDate) {
      checkDateAvailability(newDate);
      // Clear time slot if previously selected slot is now full
      if (newTimeSlot && !isSlotAvailable(newTimeSlot)) {
        setNewTimeSlot("");
      }
    }
  }, [newDate, checkDateAvailability]);

  const handleContinue = () => {
    if (consentChecked) {
      setStep("select");
    }
  };

  const handleBack = () => {
    setStep("consent");
  };

  const handleSubmit = async () => {
    if (!newDate || !newTimeSlot || !reason.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Check reschedule limit before proceeding
    if (!canReschedule) {
      toast.error(`You've reached your limit of ${maxReschedules} reschedules this billing cycle`);
      return;
    }

    setLoading(true);
    try {
      // Increment the reschedule count
      await incrementRescheduleCount(pickup.id);

      // Update the pickup with user's edit request
      // - New proposed date/time stored in counter_proposed fields
      // - Original confirmed date/time preserved in proposed_date/proposed_time_slot for history
      // - Status changes to awaiting_admin_confirmation for admin review
      // - user_message contains the required change reason
      // - updated_at timestamp is automatically updated
      const { error } = await supabase
        .from("user_pickups")
        .update({
          counter_proposed_date: format(newDate, "yyyy-MM-dd"),
          counter_proposed_time_slot: newTimeSlot,
          user_message: reason.trim(),
          status: "awaiting_admin_confirmation",
        })
        .eq("id", pickup.id);

      if (error) throw error;

      toast.success("Edit request submitted! Awaiting confirmation.");
      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error("Error editing pickup:", error);
      }
      toast.error("Failed to submit edit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isValid = newDate && newTimeSlot && reason.trim();

  // Disable dates in the past and today (need at least 1 day notice)
  const disabledDays = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return date < tomorrow;
  };

  const currentDate = new Date(pickup.proposed_date).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  // Reschedule limit reached - block the dialog
  if (!limitsLoading && !canReschedule) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="h-5 w-5 text-destructive" />
              Reschedule Limit Reached
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive">
                You've used all {maxReschedules} reschedules for this billing cycle.
              </p>
            </div>
            <p className="text-sm text-muted-foreground">
              Your reschedule limit will reset at the start of your next billing cycle. 
              If you need to reschedule urgently, please contact support.
            </p>
          </div>

          <DialogFooter>
            <Button onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Consent step content
  if (step === "consent") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Change Scheduled Pickup?
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Reschedule limit warning */}
            <div className="p-3 bg-muted rounded-lg flex items-center justify-between">
              <span className="text-sm font-medium">Reschedules remaining</span>
              <span className={cn(
                "text-sm font-bold",
                remainingReschedules <= 1 ? "text-amber-600" : "text-foreground"
              )}>
                {remainingReschedules} of {maxReschedules}
              </span>
            </div>

            <div className="space-y-3 text-sm text-muted-foreground">
              <p>
                Changing your pickup date or time will <span className="font-medium text-foreground">release your current confirmed pickup slot</span>.
              </p>
              <p>
                Your new selection must be <span className="font-medium text-foreground">reviewed and approved</span> by our logistics team.
              </p>
              <p>
                If the new time is not approved, you may be <span className="font-medium text-foreground">reassigned based on availability</span>.
              </p>
            </div>

            <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="consent"
                  checked={consentChecked}
                  onCheckedChange={(checked) => setConsentChecked(checked === true)}
                  className="mt-0.5"
                />
                <Label 
                  htmlFor="consent" 
                  className="text-sm font-medium leading-relaxed cursor-pointer text-amber-800 dark:text-amber-200"
                >
                  I understand that changing my pickup requires re-approval and forfeits my current scheduled slot
                </Label>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleContinue}
              disabled={!consentChecked || limitsLoading}
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Select new time step content
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-primary" />
            Select New Pickup Time
          </DialogTitle>
          <DialogDescription>
            Choose your preferred new date and time window.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Current scheduled time */}
          <div className="p-3 bg-muted/50 rounded-lg border">
            <p className="text-xs font-medium text-muted-foreground mb-1">
              Currently Scheduled (will be released)
            </p>
            <p className="text-sm font-medium">{currentDate}</p>
            <p className="text-sm text-muted-foreground">{pickup.proposed_time_slot}</p>
          </div>

          {/* New Date Selection */}
          <div className="space-y-2">
            <Label>New Preferred Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !newDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {newDate ? format(newDate, "PPP") : "Select a new date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={newDate}
                  onSelect={setNewDate}
                  disabled={disabledDays}
                  initialFocus
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* New Time Slot Selection */}
          <div className="space-y-2">
            <Label>New Preferred Time Window</Label>
            {!newDate ? (
              <p className="text-sm text-muted-foreground p-3 bg-muted/30 rounded-lg">
                Please select a date first to see available time slots
              </p>
            ) : checkingAvailability ? (
              <p className="text-sm text-muted-foreground p-3 bg-muted/30 rounded-lg">
                Checking availability...
              </p>
            ) : (
              <Select 
                value={newTimeSlot} 
                onValueChange={setNewTimeSlot}
                disabled={!newDate}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a time window">
                    {newTimeSlot && (
                      <span className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        {newTimeSlot}
                      </span>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((slot) => {
                    const slotStatus = getSlotStatus(slot.value);
                    const isFull = slotStatus?.isFull ?? false;
                    const spotsLeft = slotStatus?.available ?? 0;
                    
                    return (
                      <SelectItem 
                        key={slot.value} 
                        value={slot.value}
                        disabled={isFull}
                        className={cn(isFull && "opacity-50")}
                      >
                        <div className="flex items-center justify-between w-full gap-4">
                          <span>{slot.label}</span>
                          {isFull ? (
                            <span className="text-xs text-destructive font-medium flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              Unavailable
                            </span>
                          ) : spotsLeft <= 2 && spotsLeft > 0 ? (
                            <span className="text-xs text-amber-600 font-medium">
                              {spotsLeft} spot{spotsLeft !== 1 ? 's' : ''} left
                            </span>
                          ) : null}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Reason (required) */}
          <div className="space-y-2">
            <Label>
              Reason for Change <span className="text-destructive">*</span>
            </Label>
            <Textarea
              placeholder="Please explain why you need to reschedule..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              rows={3}
              required
              className={cn(!reason.trim() && "border-muted-foreground/30")}
            />
            <p className="text-xs text-muted-foreground">
              Required to help our logistics team understand your request
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={loading}
          >
            Back
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isValid || loading}
          >
            {loading ? "Submitting..." : "Request Change"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
